import React, { useState, useEffect } from "react";
import { getAllDeliveries, deleteDelivery } from "../services/deliveryService";
import { useNavigate } from "react-router-dom";

const Deliveries = () => {
  const [allDeliveries, setAllDeliveries] = useState([]); // Stores all deliveries initially
  const [filteredDeliveries, setFilteredDeliveries] = useState([]); // Stores filtered data
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [deleting, setDeleting] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeliveries();
  }, []);

  useEffect(() => {
    filterDeliveries(selectedDate);
  }, [selectedDate, allDeliveries]);

  // ✅ Fetch deliveries once and store them
  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const response = await getAllDeliveries();
      setAllDeliveries(response.data); // Store all deliveries
      filterDeliveries(selectedDate, response.data); // Apply filter immediately
    } catch (error) {
      console.error("Error fetching deliveries:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Filter deliveries from stored data without refetching
  const filterDeliveries = (date, deliveries = allDeliveries) => {
    const filteredData = deliveries.filter(
      (delivery) => delivery.delivery_date.split("T")[0] === date
    );
    setFilteredDeliveries(filteredData);
  };

  // Format date (YYYY-MM-DD to readable format)
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-500";
      case "Pending":
        return "bg-yellow-500";
      case "Cancelled":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // ✅ Show Delete Confirmation Popup
  const confirmDelete = (deliveryId) => {
    setDeleteId(deliveryId);
    setShowConfirm(true);
  };

  // ✅ Handle Delete
  const handleDelete = async () => {
    try {
      setDeleting(deleteId);
      setShowConfirm(false); // Close popup

      const response = await deleteDelivery(deleteId);
      alert(response.message);

      fetchDeliveries(); // Refetch deliveries after deletion
    } catch (error) {
      console.error("Failed to delete delivery:", error.message);
      alert("Error deleting delivery. Please try again.");
    } finally {
      setDeleting(null);
      setDeleteId(null);
    }
  };

  return (
    <div className="max-w-md mx-auto pt-1 pb-20">
      {/* Title */}
      <h2 className="text-xl font-bold mb-3 text-center">
        📦 Deliveries
      </h2>

      {/* Date Picker */}
      <div className="flex items-center gap-2 mb-3">
        <label className="text-gray-600 font-medium text-sm">
          📅 Select Date:
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border rounded-lg px-2 py-1 text-gray-700"
        />
      </div>

      {/* Create Order Button */}
      <button
        className="w-full bg-green-500 text-white py-2 rounded-lg font-semibold mb-3 hover:bg-green-600 transition"
        onClick={() => navigate("/customer")}
      >
        ➕ Create New Delivery
      </button>

      {/* Loader */}
      {loading && (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500 border-solid"></div>
        </div>
      )}

      {/* No Orders */}
      {!loading && filteredDeliveries.length === 0 && (
        <p className="text-center text-gray-600">
          No orders found for {formatDate(selectedDate)}.
        </p>
      )}

      {/* Orders List */}
      {!loading && filteredDeliveries.length > 0 && (
        <div className="flex flex-col gap-2">
          {filteredDeliveries.map((delivery) => (
            <div
              key={delivery.delivery_id}
              className="bg-white shadow-md rounded-lg p-3 border border-gray-200 flex flex-col gap-1"
            >
              {/* Header */}
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold text-gray-800">
                  #{delivery.delivery_id} - {formatDate(delivery.delivery_date)}
                </h3>
                <span
                  className={`text-white px-2 py-1 text-xs rounded-full ${getStatusColor(
                    delivery.delivery_status
                  )}`}
                >
                  {delivery.delivery_status}
                </span>
              </div>

              {/* Customer Info */}
              <p className="text-sm text-gray-700 font-medium">
                👤 {delivery.customer?.name} - 📞 {delivery.customer?.mobile}
              </p>

              {/* Product Info */}
              <p className="text-sm text-gray-700 font-medium">
                🛒 {delivery.product?.product_name} -{" "}
                {delivery.quantity_delivered} unit(s)
              </p>

              {/* Payment Info */}
              <p
                className={`text-xs font-bold ${
                  delivery.payment_status === "Paid"
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                💰 {delivery.payment_status === "Paid" ? "Paid" : "Unpaid"} | ₹
                {delivery.due_amount} Due
              </p>

              {/* Empty Bottles Collected */}
              <p className="text-xs text-gray-500">
                ♻️ Empty Bottles:{" "}
                {delivery.product_id === 1
                  ? delivery.cool_bottles_returned
                  : delivery.product_id === 2
                  ? delivery.pet_bottles_returned
                  : 0}
              </p>

              {/* View Details Button
              <button
                className="mt-2 bg-blue-500 text-white py-1 rounded-md text-center text-xs font-semibold hover:bg-blue-600 transition"
                onClick={() => navigate(`/order/${delivery.delivery_id}`)}
              >
                View Details
              </button> */}
               <div className="flex gap-2 mt-2">
                {/* ✅ Delete Button with Confirmation Popup */}
                <button
                  className="flex-1 bg-red-500 text-white py-1 rounded-md text-center text-xs font-semibold hover:bg-red-600 transition"
                  onClick={() => confirmDelete(delivery.delivery_id)}
                  disabled={deleting === delivery.delivery_id}
                >
                  {deleting === delivery.delivery_id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ Delete Confirmation Popup */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg shadow-lg w-80 text-center">
            <h3 className="text-lg font-semibold text-gray-800">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-600 my-3">
              Are you sure you want to delete this delivery? This action cannot
              be undone.
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold"
                onClick={() => setShowConfirm(false)}
              >
                No
              </button>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition"
                onClick={handleDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deliveries;
